<template>
    <div class="text-cool-gray font-bold text-xl text-center">Current Total: <span v-text="formattedTotal" class="text-white"></span></div>
    <hr class="border-purple">
    <div class="text-center flex flex-col items-center">
        <span class="bg-clip-text text-transparent font-bold text-2xl uppercase bg-gradient-title">Donate Form</span>
        <span class="text-cool-gray font-bold text-md">Step {{ step + 1 }} of {{ steps.length }}</span>
    </div>
    <keep-alive>
        <component v-bind:is="steps[step]" :dtd="props.dtd"></component>
    </keep-alive>
    <div class="flex flex-col gap-y-4 sm:flex-row sm:justify-center sm:gap-x-8 sm:gap-y-0" v-if="!isAtEnd">
        <donation-button size="large" v-if="canGoBackward" @click="previousStep">Back</donation-button>
        <donation-button appearance="primary" size="large" :disabled="!canGoForward" @click="nextStep">Next</donation-button>
    </div>
    <hr class="border-purple" v-if="!isAtEnd">
    <div class="text-center">
        <a href="#" v-if="!isAtEnd" @click.prevent="goToEnd" :disabled="!canGoForward">Skip to the end</a>
    </div>
    <div v-if="isAtEnd" class="flex flex-col items-center space-y-8">
        <div class="flex flex-col items-center space-y-2">
            <div class="text-white font-bold text-lg">100% of your donation benefits</div>

            <a :href="campaign.charity.url" target="_blank">
                <div class="bg-white max-w-[16rem] p-4 rounded-lg">
                    <img :src="campaign.charity.logo">
                </div>
            </a>
        </div>

        <form action="/" method="POST">
            <csrf-field></csrf-field>
            <!-- Some thing about sending empty form fields breaks laravel 10, so remove inputs if left blank -->
            <input v-if="name.length > 0" type="hidden" name="name" :value="name">
            <input type="hidden" name="amount" :value="amount">
            <input v-if="comment.length > 0" type="hidden" name="comments" :value="comment">

            <input v-if="character.filename != 'Link'" type="hidden" name="character" :value="character.key">

            <input v-if="pollItem" type="hidden" name="poll_item_id" :value="pollItem.id">
            <input v-if="fileDefinition && filename.length > 0" type="hidden" name="file_definition_id" :value="fileDefinition.id">
            <input v-if="fileSuggestion && filename.length > 0" type="hidden" name="filename" :value="filename">
            <template v-if="employer.length > 0 && employerFields.length > 0" v-for="field in employerFields">
                <input v-if="field.value.length > 0" type="hidden" :name="field.name" :value="field.value">
            </template>
            <donation-button type="submit" appearance="primary" size="large" @click="postForm" :disabled="loading">
                <span v-if="loading">
                    <loading-spinner></loading-spinner>
                </span>
                <span v-else>Donate</span>
            </donation-button>
        </form>
        <div class="text-cool-gray text-center">You'll be redirected to PayPal to complete the donation</div>
    </div>
    <!-- <div class="text-sm text-gray-600 text-center">
        Image credits: Devan Whitney (left) / Anna Kim Photography (right)
    </div> -->
</template>

<script setup>
import { ref, computed, provide } from 'vue';
import { formatMoney } from '../../utils';

import DonationDetails from './DonationDetails.vue';
import OverlayCharacter from './OverlayCharacter.vue';
import IncentiveSelection from './IncentiveSelection.vue';
import DonationSummary from './DonationSummary.vue';
import DonationButton from './ui/Button.vue';

const props = defineProps({
    campaign: Object,
    dtd: Boolean,
    characters: Boolean,
    initialCharacter: String,
    initialAmount: String
});

// TODO: Make this dynamic. If we've turned off certain things, then we don't need to add them as steps.
let steps;
if(props.characters) {
    steps = [
        DonationDetails,
        OverlayCharacter,
        IncentiveSelection,
        DonationSummary
    ];
} else {
    steps = [
        DonationDetails,
        IncentiveSelection,
        DonationSummary
    ];
}

const step = ref(0);

const formattedTotal = computed(() => formatMoney(props.campaign.total));

const name = ref('');
const amount = ref(props.initialAmount ?? '');
const comment = ref('');
const employer = ref('');
const employerFields = ref([]);
const character = ref({
    filename: "Link",
    name: "Link",
    key: "[Link]"
});
const poll = ref(null);
const pollItem = ref(null);
const fileDefinition = ref(null);
const fileSuggestion = ref(null);
const customFilename = ref(null);

const loading = ref(false);

const filename = computed(() => {
    if (!fileSuggestion.value) { return null }

    if (fileSuggestion.value.id == -1) {
        return customFilename.value;
    } else {
        return fileSuggestion.value.name;
    }
});

provide('name', name);
provide('amount', amount);
provide('comment', comment);
provide('employer', employer);
provide('employerFields', employerFields);
provide('character', character);
provide('poll', poll);
provide('pollItem', pollItem);
provide('fileDefinition', fileDefinition);
provide('fileSuggestion', fileSuggestion);
provide('customFilename', customFilename);

const canGoForward = computed(() => {
    switch (step.value) {
        case 0: // Donation details
            return amount.value >= 1;
        case 2:
            if (poll.value && !pollItem.value) {
                return false;
            }

            if (fileDefinition.value) {
                if (!fileSuggestion.value) {
                    return false;
                }

                if (fileSuggestion.value.id == -1 && !filename.value && (filename.value.length > fileDefinition.value.max_length || filename.value.length <= 0)) {
                    return false;
                }
            }

            return true;
        default:
            return true;
    }
});
const canGoBackward = computed(() => step.value > 0);
const isAtEnd = computed(() => step.value == steps.length - 1);

const goToEnd = () => {
    if (!canGoForward.value) { return; }
    step.value = steps.length - 1;
}
const nextStep = () => { step.value += 1 };
const previousStep = () => { step.value -= 1 };

const goToDonationDetails = () => { step.value = 0; };
const goToOverlayCharacter = () => { step.value = 1; };
const goToIncentiveSelection = () => { step.value = 2; };

provide('navigation', {
    goToDonationDetails,
    goToOverlayCharacter,
    goToIncentiveSelection
})

const postForm = () => {
    if(loading.value) { return; }
    loading.value = true;
    // now actually send the post
    document.querySelector('form').submit();
    setTimeout(() => {
        loading.value = false;
    }, 15000);
}

</script>
